import React, { Fragment, useState, useEffect, useRef, useCallback } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Alert from "../layout/Alert";
import EmailNotificationForm from "./emailNotificationForm";
import EmailNotificationConfig from "./emailNotificationConfig";
import EmailUserSelection from "./emailUserSelection";
import {
  notificationChannelTypes,
  notificationConstants,
  notificationDetailsRefreshRate,
  notificationStatusHeaders,
  emailUserTargetTypes,
  getCountryNameByCode,
  showBroadcastCountryAlert,
} from "./notificationConstant";
import { parseCsvUserIdsFromFile, stripHtmlToPlainText, getNotificationExpiryUnixSeconds, isNotificationExpiryInFuture } from "./notificationFormUtils";
import { prepareEmailHtmlForDelivery, resetNotificationEmailImageRegistry } from "./emailEditorUtils";
import { format } from "date-fns";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AppButton from "../Common/Button/Button";
import AppIconButton from "../Common/Button/IconButton";
import DataTable from "../Common/DataTable/DataTable";
import { SectionHeading } from "../Common/Typography/AppTypography";

const sectionCardSx = {
  p: { xs: 1.5, md: 2 },
  mt: { xs: 1, md: 2 },
  boxShadow: 2,
  borderRadius: 1,
  overflow: "visible",
};

const tableHeaders = notificationStatusHeaders;

const SendEmailNotification = ({
  allowedCountries,
  setAlert,
  notificationStore: { sendNotificationInProgress, sendNotificationSuccess, lastNotificationDetails },
  createEmailNotificationTask,
  getNotificationTask,
  setDialog,
  setDialogContents,
  setDialogTab,
}) => {
  const [emailNotificationData, setEmailNotificationData] = useState({ subject: "", html: "" });
  const [expiryDate, setExpiryDate] = useState(undefined);
  const [userTargetType, setUserTargetType] = useState(emailUserTargetTypes.broadcast.value);
  const [country, setCountry] = useState("None");
  const [uploadedUserIdFileInput, setUploadedUserIdFileInput] = useState("");
  const userIdCSVFileInputRef = useRef();
  const emailEditorRef = useRef(null);
  const [emailEditorKey, setEmailEditorKey] = useState(0);
  const [notificationTaskData, setNotificationTaskData] = useState(null);
  const [fetchNotificationTimeout, setFetchNotificationTimeout] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleReset = useCallback(() => {
    setEmailNotificationData({ subject: "", html: "" });
    setExpiryDate(undefined);
    setUserTargetType(emailUserTargetTypes.broadcast.value);
    setCountry("None");
    setUploadedUserIdFileInput("");
    emailEditorRef.current = null;
    resetNotificationEmailImageRegistry();
    setEmailEditorKey((key) => key + 1);
    try {
      if (userIdCSVFileInputRef.current) userIdCSVFileInputRef.current.value = "";
    } catch (_) { /* noop */ }
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!sendNotificationSuccess) return;
    handleReset();
  }, [sendNotificationSuccess, handleReset]);

  useEffect(() => {
    return () => clearTimeout(fetchNotificationTimeout);
  }, [fetchNotificationTimeout]);

  useEffect(() => {
    const expectedUserCount =
      lastNotificationDetails?.status?.taskProcessStatus?.userCount +
      lastNotificationDetails?.status?.taskProcessStatus?.userFailedCount;
    const userSentCount = lastNotificationDetails?.status?.notificationProcessStatus?.userSentCount;
    const failedUserCount =
      lastNotificationDetails?.status?.notificationProcessStatus?.failedUserSentCount +
      lastNotificationDetails?.status?.notificationProcessStatus?.tokenNotFoundUserCount +
      lastNotificationDetails?.status?.taskProcessStatus?.userFailedCount;

    if (lastNotificationDetails?.taskId) {
      clearTimeout(fetchNotificationTimeout);
      if (
        !(
          lastNotificationDetails?.status?.taskStatus === "processed" &&
          (expectedUserCount === userSentCount || expectedUserCount === failedUserCount)
        )
      ) {
        setFetchNotificationTimeout(
          setTimeout(() => {
            getNotificationTask(lastNotificationDetails?.taskId, "updateSentNotification");
          }, notificationDetailsRefreshRate)
        );
      }
    }

    if (lastNotificationDetails?.channel?.type !== notificationChannelTypes?.emailNotification?.value) {
      return;
    }

    setNotificationTaskData(
      JSON.stringify(lastNotificationDetails) !== "{}" ? {
        expectedUserCount: (
          <div title="The expected number of users that will receive the notification.">{expectedUserCount}</div>
        ),
        fullTaskId: lastNotificationDetails?.taskId,
        taskId: (
          <div>
            <div title={lastNotificationDetails?.taskId}>
              {"..." +
                lastNotificationDetails?.taskId?.substring(
                  lastNotificationDetails?.taskId?.length - 6,
                  lastNotificationDetails?.taskId?.length
                )}
            </div>
          </div>
        ),
        userCount: (
          <div title="The number of users that received the notification.">{userSentCount}</div>
        ),
        taskStatus: lastNotificationDetails?.status?.taskStatus,
        failureCount: (
          <div title="The number of users the notification failed to reach.">{failedUserCount}</div>
        ),
        notificationType:
          notificationChannelTypes[lastNotificationDetails?.channel?.type + "Notification"]?.label,
        severity: lastNotificationDetails?.severity || "-",
        createdAt: format(new Date(lastNotificationDetails?.creationDate * 1000), "dd MMM yyyy 'AT' hh:mm a"),
        expiryDate: lastNotificationDetails?.expiry
          ? format(new Date(lastNotificationDetails?.expiry * 1000), "dd MMM yyyy 'AT' hh:mm a")
          : "-",
        payload: { ...lastNotificationDetails },
        msg: {
          subject: lastNotificationDetails?.message?.subject,
          html: lastNotificationDetails?.message?.html,
        },
        action: (data) => (
          <Box sx={{ display: "flex", justifyContent: "start", alignItems: "center", width: "100%" }}>
            <AppIconButton
              variant="primary"
              size="small"
              icon={<VisibilityIcon fontSize="small" />}
              tooltip="View notification"
              onClick={() => {
                setDialog(true);
                setDialogTab(0);
                setDialogContents({ content: "notificationDetails", title: "Notification Details", data });
              }}
            />
          </Box>
        ),
      } : {}
    );
  }, [lastNotificationDetails]);

  function handleUserTargetTypeChange(nextType) {
    setUserTargetType(nextType);
    if (nextType === emailUserTargetTypes.broadcast.value) {
      setUploadedUserIdFileInput("");
      try {
        if (userIdCSVFileInputRef.current) userIdCSVFileInputRef.current.value = "";
      } catch (_) { /* noop */ }
    } else {
      setCountry("None");
    }
  }

  function submitEmailNotificationTask(notificationTaskRequest) {
    setNotificationTaskData({});
    createEmailNotificationTask(notificationTaskRequest);
    window.scrollTo(0, 0);
  }

  function handleCreateEmailNotificationTask() {
    if (!emailEditorRef.current && !emailNotificationData?.html?.trim()) {
      setAlert("Email editor is not ready. Please wait a moment and try again.", "danger");
      window.scrollTo(0, 0);
      return;
    }

    if (!emailNotificationData?.subject?.trim()) {
      setAlert("Email notification subject cannot be empty", "danger");
      window.scrollTo(0, 0);
      return;
    }
    if (!userTargetType) {
      setAlert("Please select a user type", "danger");
      window.scrollTo(0, 0);
      return;
    }
    if (!expiryDate) {
      setAlert("Invalid Expiry Type. Please select expiry time", "danger");
      window.scrollTo(0, 0);
      return;
    }
    if (!isNotificationExpiryInFuture(expiryDate)) {
      setAlert("Expiry date must be in the future. Please select a valid expiry date.", "danger");
      window.scrollTo(0, 0);
      return;
    }

    const buildNotificationTaskRequest = (htmlContent, plainText) => ({
      channel: { type: notificationChannelTypes.emailNotification.value },
      message: {
        subject: emailNotificationData.subject,
        text: plainText,
        html: htmlContent,
      },
      priority: notificationConstants.emailNotificationPriorityNormal,
      severity: notificationConstants.notifySeverityInfo,
      expiry: getNotificationExpiryUnixSeconds(expiryDate),
      users: {},
    });

    const deliverySource = emailEditorRef.current?.innerHTML || emailNotificationData.html;
    const { html: htmlContent, failed, imageCount } = prepareEmailHtmlForDelivery(deliverySource);

    if (failed.length) {
      setAlert(
        `Unable to prepare image "${failed[0].alt}" for email delivery. ${failed[0].reason}`,
        "danger"
      );
      window.scrollTo(0, 0);
      return;
    }
    if (!stripHtmlToPlainText(htmlContent) && imageCount === 0) {
      setAlert("Email notification body cannot be empty", "danger");
      window.scrollTo(0, 0);
      return;
    }

    const sendPreparedEmailNotification = (htmlContent, plainText) => {
      const notificationTaskRequest = buildNotificationTaskRequest(htmlContent, plainText);

      if (userTargetType === emailUserTargetTypes.broadcast.value) {
        if (!country || country === "") {
          setAlert("Please select a country", "danger");
          window.scrollTo(0, 0);
          return;
        }
        if (!showBroadcastCountryAlert(getCountryNameByCode(country))) {
          return;
        }
        notificationTaskRequest.users.broadcast = true;
        if (country !== "None") {
          notificationTaskRequest.users.country = country?.toLowerCase();
        }
        submitEmailNotificationTask(notificationTaskRequest);
        return;
      }

      if (!uploadedUserIdFileInput || uploadedUserIdFileInput === "") {
        setAlert("Please upload a CSV file with user IDs", "danger");
        window.scrollTo(0, 0);
        return;
      }
      if (!uploadedUserIdFileInput?.type?.includes("csv")) {
        setAlert("Please upload a csv file.", "danger");
        window.scrollTo(0, 0);
        return;
      }

      parseCsvUserIdsFromFile(uploadedUserIdFileInput)
        .then((userIds) => {
          if (!userIds?.length) {
            setAlert("CSV file does not contain any user IDs", "danger");
            window.scrollTo(0, 0);
            return;
          }
          notificationTaskRequest.users.userIds = userIds;
          submitEmailNotificationTask(notificationTaskRequest);
        })
        .catch(() => {
          setAlert("Failed to Parse User IDs Uploaded List", "danger");
        });
    };

    sendPreparedEmailNotification(htmlContent, stripHtmlToPlainText(htmlContent));
  }

  return (
    <Fragment>
      <Alert />
      <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box", pb: 2 }}>
        {notificationTaskData && JSON.stringify(notificationTaskData) !== "{}" && (
          <Paper sx={{ ...sectionCardSx, mt: 1 }}>
            <SectionHeading sx={{ mb: 1 }}>Last Sent Notification details</SectionHeading>
            <DataTable
              columns={Object.keys(tableHeaders).map((key) => ({
                key,
                label: tableHeaders[key]?.label,
                align: tableHeaders[key]?.align,
                width: tableHeaders[key]?.width,
                minWidth: tableHeaders[key]?.minWidth,
                justifyContent: tableHeaders[key]?.style?.justifyContent,
                render: (val, row) => {
                  const value = row?.hasOwnProperty(key)
                    ? typeof row[key] === "function"
                      ? row[key](row)
                      : row[key]
                    : "-";
                  const isLong = String(value).length > 200;
                  if (key === "taskId") {
                    return (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <span
                          style={{
                            whiteSpace: isLong ? "normal" : "nowrap",
                            wordBreak: isLong ? "break-word" : "normal",
                          }}
                        >
                          {value}
                        </span>
                        <AppIconButton
                          size="small"
                          variant="ghost"
                          tooltip="Copy Task ID"
                          onClick={() => {
                            setCopied(true);
                            setTimeout(() => setCopied(false), 3000);
                            navigator?.clipboard?.writeText(row?.fullTaskId);
                          }}
                          icon={copied ? <CheckCircleOutlineIcon /> : <FileCopyIcon />}
                        />
                      </Box>
                    );
                  }
                  return (
                    <span
                      style={{
                        whiteSpace: isLong ? "normal" : "nowrap",
                        wordBreak: isLong ? "break-word" : "normal",
                      }}
                    >
                      {value}
                    </span>
                  );
                },
              }))}
              data={[notificationTaskData]}
              rowKey="taskId"
              maxHeight="360px"
              minWidth="100%"
            />
          </Paper>
        )}

        <EmailNotificationForm
          messageData={emailNotificationData}
          handleMessageData={(name, value) =>
            setEmailNotificationData((prev) => ({ ...prev, [name]: value }))
          }
          editorKey={emailEditorKey}
          onEditorReady={(editor) => {
            emailEditorRef.current = editor;
          }}
          onImageUploadError={(message) => setAlert(message, "danger")}
        />

        <EmailNotificationConfig
          expiryDate={expiryDate}
          onExpiryDateChange={setExpiryDate}
        />

        <EmailUserSelection
          userTargetType={userTargetType}
          handleUserTargetTypeChange={handleUserTargetTypeChange}
          country={country}
          handleCountryChange={setCountry}
          allowedCountries={allowedCountries}
          handleUserIdFileInput={setUploadedUserIdFileInput}
          uploadedUserIdFileInput={uploadedUserIdFileInput}
          userIdCSVFileInputRef={userIdCSVFileInputRef}
        />

        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mt: 2, pb: 1 }}>
          <AppButton
            variant="primary"
            size="medium"
            onClick={handleCreateEmailNotificationTask}
            disabled={sendNotificationInProgress}
            loading={sendNotificationInProgress}
          >
            Create Notification Task
          </AppButton>
          <AppButton
            variant="ghost"
            size="medium"
            onClick={handleReset}
            disabled={sendNotificationInProgress}
          >
            Reset
          </AppButton>
        </Box>
      </Box>
    </Fragment>
  );
};

export default SendEmailNotification;
